/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";

//icons
import NewIcon from "mdi-material-ui/NewspaperPlus";
import WaitingIcon from "mdi-material-ui/TimerSand";
import RunningIcon from "mdi-material-ui/RunFast";
import CompletedIcon from "mdi-material-ui/CheckOutline";

import { showNotification } from "jazasoft";

import { colorPrimary, colorSecondary, colorOrange, colorLemom, colorGrey, colorLightGreen, colorPurple, colorTomatoRed } from "../../utils/colors";
import useQuery from "../../hooks/useQuery";
import { isEmpty } from "../../utils/helpers";
import { getBarOptionsMultiple, getLineOptions } from "../../helpers/echartHelper";
import useMutation from "../../hooks/useMutation";
import { TaskState } from "../../utils/types";
import { REFRESH_TASK } from "../../reducers/scheduledTaskReducer";

const colors = [colorSecondary.main, colorLemom.main, colorPurple.main, colorLightGreen.main, colorTomatoRed.main, colorOrange.main, colorGrey.main];

const stateColor = value => {
  switch (value) {
    case TaskState.NEW:
      return colorSecondary.main;
    case TaskState.WAITING:
      return colorOrange.main;
    case TaskState.RUNNING:
      return colorLemom.main;
    case TaskState.COMPLETED:
      return colorLightGreen.main;
    case TaskState.FAILED:
      return colorTomatoRed.main;
    case TaskState.CANCELLED:
      return colorLemom.main;
    default:
      return colorLemom.main;
  }
};

const useScheduledTask = ({ page, period }) => {
  const search = useMemo(() => {
    if (!period) return null;
    let [value, unit] = period.split("-");
    const start = moment().subtract(parseInt(value), unit).startOf("day").valueOf();
    const end = moment().endOf("day").valueOf();
    const search = `createdAt=ge=${start};createdAt=le=${end}`;
    return search;
  }, [period]);

  // api call
  const { data: tenantList = [] } = useQuery({ url: "tenants" });
  const { data: historyTasks = [] } = useQuery(page === "history" && !isEmpty(search) && { url: "scheduledTasks", params: { search } });

  //hooks
  const [mutate] = useMutation();
  const dispatch = useDispatch();

  // redux
  const tasks = useSelector(state => state.scheduledTask?.data || {});

  const taskList = page === "history" ? historyTasks : Object.keys(tasks).map(idx => tasks[idx]);

  // handle functions
  const onTaskDelete = (taskId, cb) => {
    const options = {
      url: `scheduledTasks/${taskId}`,
      method: "delete"
    };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("Task deleted sucessfully."));
        dispatch({ type: REFRESH_TASK });
        cb && cb();
      },
      onFailure: () => cb && cb()
    });
  };

  const dataSummary = useMemo(() => {
    if (page !== "realtime") return [];
    return [
      {
        label: TaskState.NEW,
        value: taskList.filter(e => e.state === TaskState.NEW).length,
        icon: <NewIcon style={{ fontSize: 48, color: colorPrimary.main }} />
      },
      {
        label: TaskState.WAITING,
        value: taskList.filter(e => e.state === TaskState.WAITING).length,
        icon: <WaitingIcon style={{ fontSize: 48, color: colorSecondary.main }} />
      },
      {
        label: TaskState.RUNNING,
        value: taskList.filter(e => e.state === TaskState.RUNNING).length,
        icon: <RunningIcon style={{ fontSize: 48, color: colorTomatoRed.main }} />
      },
      {
        label: TaskState.COMPLETED,
        value: taskList.filter(e => e.state === TaskState.COMPLETED).length,
        icon: <CompletedIcon style={{ fontSize: 48, color: colorLightGreen.main }} />
      }
    ];
  }, [JSON.stringify(taskList.map(t => t.modifiedAt))]);

  const echartOptions = useMemo(() => {
    if (page !== "realtime") return {};
    let options = {};

    //* Prepare Echart Option for Tenant Wise Task Status Chart *//
    let data = tenantList.map(e => ({
      tenantId: e.tenantId,
      ...Object.values(TaskState).reduce(
        (acc, state) => ({
          ...acc,
          [state]: taskList.filter(t => t.tenantId === e.tenantId && t.state === state).length
        }),
        {}
      )
    }));

    const sumTopLabel = ({ tenantId, ...rest }) => Object.values(rest).reduce((acc, e) => acc + e);
    options.barChartMultiple = getBarOptionsMultiple(
      data,
      e => e.tenantId,
      Object.values(TaskState).map(state => ({
        name: state,
        mapper: e => e[state]
      })),
      colors,
      { stack: "State", barWidth: 15, showAxisY: true, sumTopLabel }
    );

    //* Prepare Echart Option for Task Status Chart on timeline *//
    // ----- config + windowing -----
    const durationHours = 2;
    const now = Date.now();
    const interval = 60_000; // 1 minute

    const windowEnd = now;
    const windowStart = windowEnd - durationHours * 3_600_000;

    // align to minute boundary for stable bins
    const first = Math.floor(windowStart / interval) * interval;
    const last = Math.floor((windowEnd - 1) / interval) * interval;

    const intervals = [];
    for (let t = first; t <= last; t += interval) intervals.push(t);

    // half-open overlap: [start, end)
    const overlaps = (a, b) => a.start < b.end && b.start < a.end;

    // ----- build series with per-window single-state counting -----
    data = intervals.map(t => {
      const win = { start: t, end: t + interval };
      let New = 0,
        Waiting = 0,
        Running = 0;

      for (const task of taskList) {
        const {
          createdAt, // number (ms)
          submitAt, // number (ms) | null/undefined
          startedAt, // number (ms) | null/undefined
          finishedAt // number (ms) | null/undefined
        } = task;

        // Priority: RUNNING -> WAITING -> NEW
        // RUNNING: [startedAt, finishedAt or now)
        if (startedAt != null) {
          const runEnd = finishedAt ?? now;
          if (overlaps(win, { start: startedAt, end: runEnd })) {
            Running++;
            continue; // counted once in this window
          }
        }

        // WAITING: [submitAt, startedAt)
        if (submitAt != null && startedAt != null) {
          if (overlaps(win, { start: submitAt, end: startedAt })) {
            Waiting++;
            continue;
          }
        }

        // NEW: [createdAt, submitAt)
        if (createdAt != null && submitAt != null) {
          if (overlaps(win, { start: createdAt, end: submitAt })) {
            New++;
            continue;
          }
        }

        // Note: tasks that don't overlap any phase in this window are ignored
      }

      return { time: t, New, Waiting, Running };
    });

    options.stateTimeline = getLineOptions(
      data,
      e => moment(e.time).format("HH:mm"),
      [
        { name: TaskState.NEW, mapper: e => e[TaskState.NEW] },
        { name: TaskState.WAITING, mapper: e => e[TaskState.WAITING] },
        { name: TaskState.RUNNING, mapper: e => e[TaskState.RUNNING] }
      ],
      [colorSecondary.light, colorTomatoRed.light, colorLightGreen.main]
    );

    //* Prepare Echart Option for Processing Time *//
    // Build processing-time trend by minute of task completion
    const cList = taskList.filter(
      e =>
        e.state === TaskState.COMPLETED &&
        e.finishedAt >= windowStart && // include long-running
        e.createdAt &&
        e.submitAt &&
        e.startedAt &&
        e.finishedAt
    );

    data = intervals.map(time => {
      const list = cList.filter(t => t.finishedAt >= time && t.finishedAt < time + interval);

      const totalTime = list.length > 0 ? list.reduce((sum, t) => sum + Math.max(0, t.finishedAt - t.createdAt), 0) / list.length / 1000 : 0;

      const processingTime = list.length > 0 ? list.reduce((sum, t) => sum + Math.max(0, t.finishedAt - t.startedAt), 0) / list.length / 1000 : 0;

      return { time, totalTime, processingTime };
    });

    options.processingTimeline = getLineOptions(
      data,
      e => moment(e.time).format("HH:mm"),
      [
        { name: "Waiting + Processing Time", mapper: e => e.totalTime },
        { name: "Processing Time", mapper: e => e.processingTime }
      ],
      [colorTomatoRed.light, colorLightGreen.main],
      { area: true }
    );

    return options;
  }, [JSON.stringify(taskList.map(t => t.modifiedAt))]);

  const columns = useMemo(
    () => [
      { dataKey: "tenantId", title: "Tenant Id", filterOptions: { filter: true } },
      { dataKey: "name", title: "Name", filterOptions: { filter: true } },
      { dataKey: "poRef", title: "PO Ref" },
      { dataKey: "flow", title: "Flow" },
      {
        dataKey: "state",
        title: "State",
        render: value => (
          <span
            style={{
              padding: "3px 6px",
              borderRadius: 4,
              color: "white",
              fontSize: 12,
              backgroundColor: stateColor(value)
            }}>
            {value}
          </span>
        ),
        filterOptions: { filter: true }
      },
      { dataKey: "createdAt", title: "Created At", render: value => moment(value).format("lll") },
      { dataKey: "createdBy", title: "Created By", filterOptions: { filter: true } },
      { dataKey: "waitingTime", title: "Waiting Time", render: value => (value ? `${value} sec` : "-") },
      { dataKey: "processingTime", title: "Processing Time", render: value => (value ? `${value} sec` : "-") },
      { dataKey: "error", title: "Error" }
    ],
    []
  );

  const rows = useMemo(() => {
    return taskList
      .sort((a, b) => b.createdAt - a.createdAt)
      .map(task => {
        const meta = JSON.parse(task.meta || "{}");
        const { poRef, styles, colors, productName, lot, part } = meta.planningDto || {};
        return {
          ...task,
          flow: `${styles}, ${colors}, ${productName}, ${part}, Lot${lot}`,
          poRef,
          waitingTime: task.startedAt ? Math.round((task.startedAt - task.createdAt) / 1000) : 0,
          processingTime: task.startedAt && task.finishedAt ? Math.round((task.finishedAt - task.startedAt) / 1000) : 0
        };
      });
  });

  return { taskList, dataSummary, echartOptions, columns, rows, onTaskDelete };
};

export default useScheduledTask;
